import type { IConfigCatClient, User } from "configcat-common";
import { PollingMode } from "configcat-common";
import { DataGovernance, getClient } from "configcat-js";

import { isMockMode, mockLatencyMs } from "./local-mock";
import { createRetryPolicy } from "./retry";

export interface Config {
  newCoreApiRootUri: string;
  newDashboardRootUrl: string;
  azureAdB2cInstance: string;
  azureAdB2cDomain: string;
  azureAdB2cNewDashboardClientId: string;
  azureAdB2cCoreApiScope: string;
  azureAdB2cDashboardSignInPolicyId: string;
  azureAdB2cPoliciesSignUpV2: string;
  azureApplicationInsightsInstrumentationKey: string | undefined;
  envLongName: string;
  intercomDashboardAppId: string;
  preprDashboardUrl: string;
  authenticationServerRootUrl: string;
  showTicketPortfolioSettings: boolean;
  isAiTicketResponsibilitySuggestionAvailable: boolean;
  isAdminMultiProjectsInviteAvailable: boolean;
  surveyAiFollowUpSuggestions: boolean;
  isPortfolioBulkSurveyCreateAvailable: boolean;
  isPortfolioBulkUploadAddressAvailable: boolean;
  isFeelAtHomeSuggestionsWidgetEnabled: boolean;
  isCommunityFeedInsightsWidgetEnabled: boolean;
  isCommunityFeedSentimentAvailable: boolean;
  /**
   * Controls whether Explore more is shown in project and portfolio analytics navigation.
   * Standard analytics navigation and direct routes are intentionally independent of this flag.
   */
  isAnalyticsInsightsAvailable: boolean;
  isAnalyticsV2Row1Available: boolean;
  isAnalyticsV2Row2Available: boolean;
  isAnalyticsV2Row3Available: boolean;
  isDashboardV3Available: boolean;
  isMonthlyLivingExperienceReportEmailEnabled: boolean;
  isNobiAvailable: boolean;
  isDashboardContextualHelpAvailable: boolean;
  useNewUnifiedPostCreation: boolean;
}

const developmentConfig = {
  newCoreApiRootUri: import.meta.env.VITE_API_BASE_URL,
  newDashboardRootUrl: import.meta.env.VITE_DASHBOARD_ROOT_URL,
  azureAdB2cInstance: import.meta.env.VITE_AZURE_AD_INSTANCE,
  azureAdB2cDomain: import.meta.env.VITE_AZURE_AD_DOMAIN,
  azureAdB2cNewDashboardClientId: import.meta.env.VITE_AZURE_AD_CLIENT_ID,
  azureAdB2cCoreApiScope: import.meta.env.VITE_AZURE_AD_SCOPE,
  azureAdB2cDashboardSignInPolicyId: import.meta.env.VITE_AZURE_AD_SIGN_IN_POLICY_ID,
  azureAdB2cPoliciesSignUpV2: import.meta.env.VITE_AZURE_AD_SIGN_UP_POLICY_ID,
  azureApplicationInsightsInstrumentationKey: undefined,
  envLongName: import.meta.env.VITE_MOCK_API_IN_DEVELOPMENT === "true" ? "Mock" : "Local",
  intercomDashboardAppId: import.meta.env.VITE_INTERCOM_APP_ID,
  preprDashboardUrl: import.meta.env.VITE_PREPR_DASHBOARD_URL,
  authenticationServerRootUrl: import.meta.env.VITE_AUTHENTICATION_SERVER_ROOT_URL,
  showTicketPortfolioSettings: true,
  isAiTicketResponsibilitySuggestionAvailable: true,
  isAdminMultiProjectsInviteAvailable: true,
  surveyAiFollowUpSuggestions: true,
  isPortfolioBulkSurveyCreateAvailable: true,
  isPortfolioBulkUploadAddressAvailable: true,
  isFeelAtHomeSuggestionsWidgetEnabled: true,
  isCommunityFeedInsightsWidgetEnabled: true,
  isCommunityFeedSentimentAvailable: false,
  isAnalyticsInsightsAvailable: true,
  isAnalyticsV2Row1Available: true,
  isAnalyticsV2Row2Available: true,
  isAnalyticsV2Row3Available: true,
  isDashboardV3Available: true,
  isMonthlyLivingExperienceReportEmailEnabled: true,
  isNobiAvailable: true,
  isDashboardContextualHelpAvailable: true,
  useNewUnifiedPostCreation: true,
} satisfies Config;

let configClient: {
  snapshot: () => {
    getValue<T extends keyof Config, TValue extends Config[T] = Config[T]>(
      key: T,
      defaultValue?: TValue,
      user?: User,
    ): Config[T];
  };
  getAllKeysAsync: IConfigCatClient["getAllKeysAsync"];
};

// Mock builds use the local config too: ConfigCat would point them at a real API instead of MSW.
if (import.meta.env.DEV || isMockMode || import.meta.env.MODE === "test" || import.meta.env.STORYBOOK === "true") {
  configClient = {
    snapshot: () => ({
      getValue: (key) => developmentConfig[key],
    }),
    // Mock latency stands in for the ConfigCat request, so local traces keep this startup step.
    getAllKeysAsync: async () => {
      if (mockLatencyMs > 0) {
        await new Promise((resolve) => setTimeout(resolve, mockLatencyMs));
      }

      return Object.keys(developmentConfig);
    },
  };
} else {
  // Use dynamic config cat key configuration in non development environments
  const configCatSDKKeyProperty = "AOP_CONFIGCAT_KEY";

  if (typeof window[configCatSDKKeyProperty] !== "string" || !window[configCatSDKKeyProperty]) {
    throw new Error(
      `Configuration Problem: Incorrect configcat SDK key. In production the configcat SDK key for the API has to be set on the window instance at 'window.${configCatSDKKeyProperty}' as string, found '${window[configCatSDKKeyProperty] as any}'.`,
    );
  }

  configClient = getClient(window[configCatSDKKeyProperty], PollingMode.LazyLoad, {
    dataGovernance: DataGovernance.EuOnly,
    cacheTimeToLiveSeconds: 600,
  }) as typeof configClient;
}

const retryPolicy = createRetryPolicy({
  delay: (attempt) => 500 * Math.pow(2, attempt - 1), // 500ms, 1s, 2s, 4s, 8s
  maxAttempts: 5,
});

export async function getConfigSnapshot(): Promise<ReturnType<(typeof configClient)["snapshot"]>> {
  // Make sure configcat has loaded the configuration. Better than forceRefreshAsync,
  // because it's not forcing a refresh and it can potentially rely on cached values.
  // Also retry because it's the first network request in the app and we want to make sure it's reliable.
  await retryPolicy.execute(() => configClient.getAllKeysAsync());

  return configClient.snapshot();
}

export type ConfigSnapshot = Awaited<ReturnType<typeof getConfigSnapshot>>;
export type ConfigUser = User;
